import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { AdminAreaChart, AdminBarChart, DistributionList } from '../../components/admin/AdminChart';
import { AdminMetricCard, AdminPageTitle, DateRange, Panel, StatusBadge } from '../../components/admin/AdminPrimitives';
import { Button } from '../../components/ui/Button';
import { activitySeries, activitySeries12m, activitySeries90, adminReports, dashboardMetrics, dashboardOps, habitCompletion, totalUsers } from '../../data/admin';
import { useLocale } from '../../contexts/LocaleContext';
export function AdminDashboard() {
  const {
    tl
  } = useLocale();
  const navigate = useNavigate();
  const [range, setRange] = useState('30d');

  // The period selector drives ONLY the period-scoped charts below.
  // KPI cards keep their own fixed windows (today, 7d, 30d, now).
  const series = range === '7d' ? activitySeries.slice(-7) : range === '30d' ? activitySeries : range === '90d' ? activitySeries90 : activitySeries12m;
  const periodLabel: Record<string, string> = {
    '7d': 'últimos 7 dias',
    '30d': 'últimos 30 dias',
    '90d': 'últimos 90 dias',
    '12m': 'últimos 12 meses · média diária por mês'
  };
  const pending = adminReports.filter(report => report.status === 'pending');
  return <div className="space-y-4" data-id="element-1601" data-mp="1zrbrizs913ps">
      <AdminPageTitle title="Dashboard" subtitle={`Saúde operacional da plataforma. ${totalUsers} usuários cadastrados. Todos os números de saúde são agregados. O período selecionado vale para os gráficos marcados com ele; os indicadores mantêm a janela indicada em cada card.`} action={<DateRange value={range} onChange={setRange} data-id="element-1603" data-mp="23rvpu6ok6ddi" />} data-id="element-1602" data-mp="1zsmx95598ljj" />

      <section aria-label="Indicadores principais" className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-1604" data-mp="3k75li06saz48">
        {dashboardMetrics.map(metric => <AdminMetricCard key={metric.id} label={tl(metric.label)} value={metric.value} delta={metric.delta} hint={metric.hint ? tl(metric.hint) : undefined} accent={metric.accent} data-id="element-1605" data-mp="2cv6a7hz8ch5g" />)}
      </section>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1.6fr_1fr]" data-id="element-1606" data-mp="2lu1gsv66rt9a">
        <AdminAreaChart title={`Atividade da plataforma · ${periodLabel[range]}`} question="O uso diário e os Daily Checks estão acompanhando o crescimento?" data={series} xKey="day" series={[{
        id: 'dau',
        name: 'Usuários ativos'
      }, {
        id: 'checks',
        name: 'Daily Checks'
      }]} height={280} data-id="element-1607" data-mp="2quffvnpt7sv6" />

        <div className="space-y-3" data-id="element-1608" data-mp="27xnzbm1njrut">
          <section aria-label="Operação" className="grid grid-cols-2 gap-3" data-id="element-1609" data-mp="1agp8a6zhemgs">
            {dashboardOps.map(metric => <AdminMetricCard key={metric.id} label={tl(metric.label)} value={metric.value} delta={metric.delta} hint={metric.hint ? tl(metric.hint) : undefined} accent={metric.accent} data-id="element-1610" data-mp="1fe7jy54vvfsh" />)}
          </section>

          <Panel title="Denúncias aguardando decisão" hint="Ordenadas pela mais antiga." action={<Button variant="outline" onClick={() => navigate('/admin/denuncias')} data-id="element-1612" data-mp="oxopyrfbfxkl">
                Abrir fila
                <ArrowRightIcon size={15} data-id="element-1613" data-mp="k5nf99xsqewo" />
              </Button>} data-id="element-1611" data-mp="xzxyxcuxrivv">
            <ul className="space-y-2" data-id="element-1614" data-mp="f6u59rhpx619">
              {pending.map(report => <li key={report.id} className="flex items-start justify-between gap-2 border-b border-sand-line pb-2 last:border-b-0 last:pb-0" data-id="element-1615" data-mp="a90yaw8lc7zx">
                  <span className="min-w-0" data-id="element-1616" data-mp="17av6t5cjkmb">
                    <span className="bee-num block text-[11px] font-bold text-gold-deep" data-id="element-1617" data-mp="18cfzpc5zlh8">
                      {report.id}
                    </span>
                    <span className="line-clamp-1 block text-[12.5px] text-deep" data-id="element-1618" data-mp="25c722w3t7y9b">
                      {tl(report.reason)}
                    </span>
                    <span className="bee-num block text-[11px] text-muted" data-id="element-1619" data-mp="1rk0mpc8hdvax">
                      {report.date}
                    </span>
                  </span>
                  <StatusBadge status={report.status} label="Pendente" data-id="element-1620" data-mp="1hlthfqnk11or" />
                </li>)}
            </ul>
          </Panel>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2" data-id="element-1621" data-mp="18lellxcys470">
        <AdminBarChart title="Conclusão por hábito · últimos 30 dias" question="Qual hábito está segurando a conclusão dos 100 Bee?" data={habitCompletion} xKey="habit" series={[{
        id: 'completion',
        name: 'Concluído (%)'
      }]} height={260} data-id="element-1622" data-mp="18mzi3siu9x7i" />

        <Panel title="Abandono por hábito · últimos 30 dias" hint="Onde os usuários iniciam o Daily Check, mas não concluem o hábito." data-id="element-1623" data-mp="37xr23f51w5zv">
          <DistributionList items={habitCompletion.map(habit => ({
          label: habit.habit,
          value: habit.dropoff,
          secondary: `${habit.dropoff}% de abandono`
        }))} data-id="element-1624" data-mp="3d4fdyg4m2aj4" />
          <p className="mt-3 text-[11.5px] leading-snug text-muted" data-id="element-1625" data-mp="3m1y41yx43f1v">
            Abandono é a proporção de usuários que abriram o Daily Check e não marcaram o
            hábito. Nenhum registro individual é exibido aqui.
          </p>
        </Panel>
      </div>
    </div>;
}